import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { adminNavTo } from '../utils/mailAlertHost'
import { useAuth } from '../contexts/AuthContext'

function NavDrop({
  label,
  active,
  children,
}: {
  label: string
  active: boolean
  children: ReactNode
}) {
  return (
    <details className={`nav-admin-dd${active ? ' is-active' : ''}`}>
      <summary className="nav-admin-dd-sum">{label}</summary>
      <div className="nav-dd-panel">{children}</div>
    </details>
  )
}

export default function AdminPortalNavLinks() {
  const location = useLocation()
  const { isAdmin, canAccessTaskBoardV2 } = useAuth()
  const metaAiEnabled = useAuth().user?.feature_flags?.meta_ai === true
  const p = location.pathname
  const grabPanelBase = (import.meta.env.VITE_GRAB_APP_PUBLIC_URL || '').trim().replace(/\/$/, '')

  useEffect(() => {
    const isDesktopNav = () => window.matchMedia?.('(min-width: 901px)')?.matches ?? true

    const positionFloatingDd = (detail: HTMLDetailsElement) => {
      if (!isDesktopNav()) return
      const sum =
        detail.querySelector<HTMLElement>('summary.nav-admin-dd-sum') ||
        detail.querySelector<HTMLElement>('summary')
      const panel = detail.querySelector<HTMLElement>('.nav-dd-panel')
      if (!sum || !panel) return
      const r = sum.getBoundingClientRect()
      const minW = 240
      const vw = window.innerWidth || 800
      const left = Math.max(8, Math.min(r.left, vw - minW - 8))
      const top = r.bottom + 4
      panel.style.position = 'fixed'
      panel.style.left = `${left}px`
      panel.style.top = `${top}px`
      panel.style.right = 'auto'
      panel.style.bottom = 'auto'
      panel.style.zIndex = '20050'
      panel.style.maxHeight = `calc(100vh - ${top + 8}px)`
      panel.style.overflowY = 'auto'
      panel.style.overflowX = 'hidden'
      panel.setAttribute('data-nav-dd-floating', '1')
    }

    const clearFloatingDd = (detail: HTMLDetailsElement) => {
      const panel = detail.querySelector<HTMLElement>('.nav-dd-panel')
      if (!panel || panel.getAttribute('data-nav-dd-floating') !== '1') return
      panel.removeAttribute('data-nav-dd-floating')
      ;['position', 'left', 'top', 'right', 'bottom', 'zIndex', 'maxHeight', 'overflowY', 'overflowX'].forEach(
        (k) => {
          panel.style[k as 'position'] = ''
        },
      )
    }

    let ddRaf = 0
    const syncOpenFloatingDd = () => {
      if (!isDesktopNav()) {
        document.querySelectorAll('.nav-admin-dd').forEach((d) => clearFloatingDd(d as HTMLDetailsElement))
        return
      }
      if (ddRaf) cancelAnimationFrame(ddRaf)
      ddRaf = requestAnimationFrame(() => {
        ddRaf = 0
        document.querySelectorAll('.nav-admin-dd[open]').forEach((d) => positionFloatingDd(d as HTMLDetailsElement))
      })
    }

    const onToggle = (e: Event) => {
      const t = e.target as HTMLDetailsElement | null
      if (!t || !t.classList || !t.classList.contains('nav-admin-dd')) return
      if (t.open) {
        document.querySelectorAll('.nav-admin-dd').forEach((el) => {
          if (el !== t) {
            el.removeAttribute('open')
            clearFloatingDd(el as HTMLDetailsElement)
          }
        })
        positionFloatingDd(t)
      } else {
        clearFloatingDd(t)
      }
    }
    document.addEventListener('toggle', onToggle, true)

    const closeDd = (dd: Element | null) => {
      if (dd && dd instanceof HTMLDetailsElement) dd.removeAttribute('open')
    }

    const onDocClickCapture = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null
      const link = el?.closest?.('a.nav-dd-link')
      if (link) {
        closeDd(link.closest('.nav-admin-dd'))
      }
    }
    document.addEventListener('click', onDocClickCapture, true)

    const onDocClickBubble = (e: MouseEvent) => {
      if (!isDesktopNav()) return
      const el = e.target as HTMLElement | null
      if (!el?.closest) return
      if (el.closest('.nav-admin-dd')) return
      document.querySelectorAll('.nav-admin-dd[open]').forEach((d) => {
        d.removeAttribute('open')
      })
    }
    document.addEventListener('click', onDocClickBubble)
    window.addEventListener('scroll', syncOpenFloatingDd, true)
    window.addEventListener('resize', syncOpenFloatingDd)

    return () => {
      document.removeEventListener('toggle', onToggle, true)
      document.removeEventListener('click', onDocClickCapture, true)
      document.removeEventListener('click', onDocClickBubble)
      window.removeEventListener('scroll', syncOpenFloatingDd, true)
      window.removeEventListener('resize', syncOpenFloatingDd)
      if (ddRaf) cancelAnimationFrame(ddRaf)
      document.querySelectorAll('.nav-admin-dd').forEach((d) => clearFloatingDd(d as HTMLDetailsElement))
    }
  }, [])

  const ddTg =
    p.startsWith('/admin/telegram-portal-bot') ||
    p.startsWith('/admin/monitoring_tg') ||
    p.startsWith('/admin/website-monitoring') ||
    p.startsWith('/admin/mail-alert')
  const ddAn = p.startsWith('/meta-ai') || p.startsWith('/google-ai') || p.startsWith('/admin/analytics/') ||
    p.startsWith('/admin/fix-zone') ||
    p.startsWith('/employer/system-ai-analytics') ||
    p.startsWith('/admin/system-ai-analytics') ||
    p.startsWith('/global-analytics') ||
    p.startsWith('/admin/landing') ||
    p.startsWith('/admin/blog') ||
    p.startsWith('/admin/case_approve') ||
    p.startsWith('/admin/cases') ||
    p.startsWith('/admin/portfolio') ||
    p.startsWith('/admin/ai-visibility') ||
    p.startsWith('/admin/creative-calendar/meta-overview')
  const ddTasks =
    p.startsWith('/admin/designer-tasks') ||
    p.startsWith('/admin/task-2-0') ||
    p.startsWith('/project_status') ||
    p.startsWith('/admin/dailies') ||
    p.startsWith('/admin/work-audit') ||
    p.startsWith('/admin/reglements')
  const adminHubActive = p === '/admin' || p.startsWith('/admin/telegram-portal-bot')

  return (
    <>
      <a href={adminNavTo('/home')} className={p.includes('/home') ? 'nav-link active' : 'nav-link'}>
        Главная
      </a>
      <a href={adminNavTo('/admin/clients')} className={p.includes('/clients') ? 'nav-link active' : 'nav-link'}>
        Клиенты
      </a>
      <Link to={adminNavTo('/admin/slack')} className={p.startsWith('/admin/slack') ? 'nav-link active' : 'nav-link'}>
        Slack
      </Link>
      <a href={adminNavTo('/admin')} className={adminHubActive ? 'nav-link active' : 'nav-link'}>
        Админ
      </a>

      <a href={adminNavTo('/admin/alexcrm')} className={p.startsWith('/admin/alexcrm') ? 'nav-link active' : 'nav-link'}>
        ALexCRM
      </a>
      <NavDrop label="ТГ" active={ddTg}>
        <a
          href="/admin/telegram-portal-bot"
          className={p.startsWith('/admin/telegram-portal-bot') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          ТГ бот
        </a>
        <Link
          to="/admin/monitoring_tg"
          className={p.startsWith('/admin/monitoring_tg') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          TG мониторинг
        </Link>
        <a
          href="https://monitoring.alex-digital.io/"
          target="_blank"
          rel="noopener noreferrer"
          className={
            (typeof window !== 'undefined' && window.location.hostname === 'monitoring.alex-digital.io') ||
            p.startsWith('/admin/website-monitoring')
              ? 'nav-dd-link active'
              : 'nav-dd-link'
          }
        >
          Сайты · uptime
        </a>
      </NavDrop>

      {!metaAiEnabled&&<NavDrop
        label="КреоПортал"
        active={p.startsWith('/admin/creative-calendar') && !p.startsWith('/admin/creative-calendar/meta-overview')}
      >
        <Link
          to={adminNavTo('/admin/creative-calendar')}
          className={
            p === '/admin/creative-calendar' || p.startsWith('/admin/creative-calendar/campaign/')
              ? 'nav-dd-link active'
              : 'nav-dd-link'
          }
        >
          Календарь Meta
        </Link>
        <Link
          to={adminNavTo(metaAiEnabled?'/meta-ai?view=meta-overview':'/admin/creative-calendar/meta-overview')}
          className={p.startsWith('/admin/creative-calendar/meta-overview') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Сводка Meta (все клиенты)
        </Link>
      </NavDrop>}

      <NavDrop label="Аналитика" active={ddAn || p.startsWith('/admin/score-learning')}>
        <Link hidden={!metaAiEnabled} aria-hidden={!metaAiEnabled} to={adminNavTo('/meta-ai')} className={p.startsWith('/meta-ai') ? 'nav-dd-link active' : 'nav-dd-link'}>META-AI</Link>
        <Link to={adminNavTo('/google-ai')} className={p.startsWith('/google-ai') ? 'nav-dd-link active' : 'nav-dd-link'}>GOOGLE-AI</Link>
        {isAdmin && <a href={adminNavTo('/admin/analytics/business')} className={p === '/admin/analytics/business' ? 'nav-dd-link active' : 'nav-dd-link'}>Расчёты по бизнесу</a>}
        {isAdmin && <a href={adminNavTo('/admin/analytics/beauty')} className={p === '/admin/analytics/beauty' ? 'nav-dd-link active' : 'nav-dd-link'}>Beauty</a>}
        {isAdmin && <a href={adminNavTo('/admin/analytics/restaurant')} className={p === '/admin/analytics/restaurant' ? 'nav-dd-link active' : 'nav-dd-link'}>Restaurant</a>}
        {isAdmin && <Link to={adminNavTo('/admin/score-learning')} className={p.startsWith('/admin/score-learning') ? 'nav-dd-link active' : 'nav-dd-link'}>ИИ дофамин</Link>}
        <Link to={adminNavTo('/admin/fix-zone')} className={p.startsWith('/admin/fix-zone') ? 'nav-dd-link active' : 'nav-dd-link'}>
          Зона Фикс
        </Link>
        <Link
          to={adminNavTo('/admin/system-ai-analytics')}
          className={
            p.startsWith('/employer/system-ai-analytics') || p.startsWith('/admin/system-ai-analytics')
              ? 'nav-dd-link active'
              : 'nav-dd-link'
          }
        >
          ИИ аналитика
        </Link>
        <a
          href={adminNavTo('/global-analytics')}
          className={p.startsWith('/global-analytics') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Глоб. аналитика
        </a>
        <Link
          to={adminNavTo(metaAiEnabled?'/meta-ai?view=meta-overview':'/admin/creative-calendar/meta-overview')}
          className={p.startsWith('/admin/creative-calendar/meta-overview') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Аналитика компаний Meta
        </Link>
        <Link to={adminNavTo('/admin/landing')} className={p.includes('/admin/landing') ? 'nav-dd-link active' : 'nav-dd-link'}>
          Лендинг
        </Link>
        <Link to={adminNavTo('/admin/blog')} className={p.startsWith('/admin/blog') ? 'nav-dd-link active' : 'nav-dd-link'}>
          Статьи
        </Link>
        <Link to={adminNavTo('/admin/cases')} className={p.startsWith('/admin/cases') ? 'nav-dd-link active' : 'nav-dd-link'}>
          Кейсы
        </Link>
        <Link
          to={adminNavTo('/admin/case_approve')}
          className={p.startsWith('/admin/case_approve') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Кейсы · одобрение
        </Link>
        <Link
          to={adminNavTo('/admin/portfolio')}
          className={p.startsWith('/admin/portfolio') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Настройка портфолио
        </Link>
        <Link
          to={adminNavTo('/admin/ai-visibility')}
          className={p.startsWith('/admin/ai-visibility') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          AI‑видимость
        </Link>
        <a
          href={adminNavTo('/admin/work-audit-calendar')}
          className={p.startsWith('/admin/work-audit') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Единый трекер работ (календарь)
        </a>
      </NavDrop>

      <NavDrop label="Задачи" active={ddTasks}>
        <a
          href={adminNavTo('/admin/designer-tasks')}
          className={p.startsWith('/admin/designer-tasks') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Задачи дизайнеров
        </a>
        {(isAdmin || canAccessTaskBoardV2) && (
          <>
            <Link
              to={adminNavTo('/admin/task-2-0')}
              className={
                p.startsWith('/admin/task-2-0')
                  ? 'nav-dd-link active'
                  : 'nav-dd-link'
              }
            >
              Задачи 2.0 — вся команда
            </Link>
          </>
        )}
        <a
          href={adminNavTo('/project_status')}
          className={
            p.startsWith('/project_status') && !p.startsWith('/project_status/tasks')
              ? 'nav-dd-link active'
              : 'nav-dd-link'
          }
        >
          Статусы проектов
        </a>
        <a
          href={adminNavTo('/project_status/tasks')}
          className={p.startsWith('/project_status/tasks') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Список поставленных задач
        </a>
        <Link to={adminNavTo('/admin/dailies')} className={p.startsWith('/admin/dailies') ? 'nav-dd-link active' : 'nav-dd-link'}>
          Дейлики
        </Link>
        <a
          href={adminNavTo('/admin/work-audit-calendar')}
          className={p.startsWith('/admin/work-audit') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Трекер работ (календарь)
        </a>
        <Link
          to={adminNavTo('/admin/reglements')}
          className={p.startsWith('/admin/reglements') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Регламенты
        </Link>
      </NavDrop>

      <Link
        to={adminNavTo('/admin/portal-users')}
        className={p.startsWith('/admin/portal-users') ? 'nav-link active' : 'nav-link'}
      >
        Пользователи
      </Link>
      <NavDrop
        label="Сотрудники"
        active={p.startsWith('/admin/employees') || p.startsWith('/admin/employee-effectiveness')}
      >
        <Link
          to={adminNavTo('/admin/employees')}
          className={p.startsWith('/admin/employees') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Список
        </Link>
        <Link
          to={adminNavTo('/admin/employee-effectiveness')}
          className={p.startsWith('/admin/employee-effectiveness') ? 'nav-dd-link active' : 'nav-dd-link'}
        >
          Эффективность
        </Link>
      </NavDrop>
      <Link
        to={adminNavTo('/google_api')}
        className={p.startsWith('/google_api') ? 'nav-link active' : 'nav-link'}
      >
        GOOGLE API
      </Link>
      <a href={adminNavTo('/yandex_api')} className={p.startsWith('/yandex_api') ? 'nav-link active' : 'nav-link'}>
        ЯНДЕКС ДИРЕКТ API
      </a>
      <Link
        to={adminNavTo('/facebook_api')}
        className={p.startsWith('/facebook_api') ? 'nav-link active' : 'nav-link'}
      >
        FACEBOOK API
      </Link>
      <Link
        to={adminNavTo('/facebook_api/alerts')}
        className={p.startsWith('/facebook_api/alerts') ? 'nav-link active' : 'nav-link'}
      >
        META ALERT
      </Link>
      <Link
        to={adminNavTo('/crm_api')}
        className={p.startsWith('/crm_api') ? 'nav-link active' : 'nav-link'}
      >
        CRM API
      </Link>      <a href={adminNavTo('/settings')} className={p.startsWith('/settings') ? 'nav-link active' : 'nav-link'}>
        Настройки
      </a>
      <a
        href={adminNavTo('/checkin/clients')}
        className={p.startsWith('/checkin/') ? 'nav-link active' : 'nav-link'}
        title="CheckIn клиента"
      >
        CheckIn
      </a>
      <a href={adminNavTo('/seo')} className={p.startsWith('/seo') ? 'nav-link active' : 'nav-link'}>
        SEO
      </a>
      {grabPanelBase ? (
        <NavDrop label="Grab" active={p.startsWith('/aggregator/')}>
          <a
            href={adminNavTo('/aggregator/grab-gojek')}
            className={p.startsWith('/aggregator/') ? 'nav-dd-link active' : 'nav-dd-link'}
          >
            Агрегаторы
          </a>
          <a href={`${grabPanelBase}/`} className="nav-dd-link" target="_blank" rel="noopener noreferrer">
            GrabFood панель
          </a>
        </NavDrop>
      ) : (
        <a href={adminNavTo('/aggregator/grab-gojek')} className={p.startsWith('/aggregator/') ? 'nav-link active' : 'nav-link'}>
          Агрегаторы
        </a>
      )}
      <Link to={adminNavTo('/admin/app-clients')} className={p.startsWith('/admin/app-clients') ? 'nav-link active' : 'nav-link'}>APP КЛИЕНТЫ</Link>
      <Link to={adminNavTo('/admin/appal')} className={p.startsWith('/admin/appal') ? 'nav-link active' : 'nav-link'}>
        alex-digital ADS
      </Link>
      <Link to={adminNavTo('/admin/ads-app')} className={p.startsWith('/admin/ads-app') ? 'nav-link active' : 'nav-link'}>
        ADS — токен app
      </Link>
      <a href={adminNavTo('/admin/insights')} className={p.startsWith('/admin/insights') ? 'nav-link active' : 'nav-link'}>
        Активность
      </a>
      <Link to={adminNavTo('/admin/add-client')} className={p.includes('/add-client') ? 'nav-link active' : 'nav-link'}>
        Новый клиент
      </Link>
    </>
  )
}
